import { useEffect, useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Mail, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import { toast } from '@/hooks/use-toast';

interface EmailVerificationGuardProps {
  children: React.ReactNode;
}

export function EmailVerificationGuard({ children }: EmailVerificationGuardProps) {
  const { user, session } = useAuth();
  const [isVerified, setIsVerified] = useState<boolean | null>(null);
  const [isResending, setIsResending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  useEffect(() => {
    if (user) {
      // Check if email is confirmed
      const verified = !!user.email_confirmed_at;
      setIsVerified(verified);
      
      if (!verified) {
        console.log('Email not verified for user:', user.email);
      }
    }
  }, [user, session]);

  useEffect(() => {
    // Cooldown timer
    if (resendCooldown > 0) {
      const timer = setTimeout(() => {
        setResendCooldown(resendCooldown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const handleResendEmail = async () => {
    if (!user?.email) return;

    setIsResending(true);
    try {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email: user.email,
        options: {
          emailRedirectTo: import.meta.env.PROD ? 'https://dinoreads.com/' : `${window.location.origin}/`,
        },
      });

      if (error) throw error;

      toast({
        title: "Verification email sent!",
        description: "Check your inbox for the verification link.",
      });
      
      // Set 60 second cooldown
      setResendCooldown(60);
    } catch (error: any) {
      console.error('Resend verification error:', error);
      toast({
        title: "Failed to resend email",
        description: error.message || "Please try again later.",
        variant: "destructive",
      });
    } finally {
      setIsResending(false);
    }
  };

  const handleRefreshStatus = async () => {
    try {
      // Refresh the session to get latest user data
      const { data: { session: newSession }, error } = await supabase.auth.refreshSession();
      
      if (error) throw error;
      
      if (newSession?.user?.email_confirmed_at) {
        setIsVerified(true);
        toast({
          title: "Email verified! ✅",
          description: "Welcome to Dino! Your account is now active.",
        });
      } else {
        toast({
          title: "Email not verified yet",
          description: "Please check your inbox and click the verification link.",
          variant: "destructive",
        });
      }
    } catch (error: any) {
      console.error('Refresh session error:', error);
      toast({
        title: "Failed to refresh",
        description: "Please try signing in again.",
        variant: "destructive",
      });
    }
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    window.location.href = '/auth';
  };

  // If no user (public routes like /auth), don't block rendering
  if (!user) {
    return <>{children}</>;
  }

  // Still checking verification status for authenticated user
  if (isVerified === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  // Email not verified - show verification required screen
  if (!isVerified && user) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md card-elevated">
          <CardHeader className="text-center space-y-4">
            <div className="mx-auto w-16 h-16 bg-primary/20 rounded-full flex items-center justify-center">
              <Mail className="w-8 h-8 text-primary" />
            </div>
            <div className="space-y-2">
              <CardTitle className="text-2xl font-heading">
                Verify Your Email 📧
              </CardTitle>
              <CardDescription>
                We sent a verification link to <strong>{user.email}</strong>
              </CardDescription>
            </div>
          </CardHeader>

          <CardContent className="space-y-6">
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Please check your email and click the verification link to access your account.
                Don't forget to check your spam folder!
              </AlertDescription>
            </Alert>

            <div className="space-y-3">
              <Button
                onClick={handleRefreshStatus}
                variant="default"
                className="w-full"
              >
                <CheckCircle2 className="w-4 h-4 mr-2" />
                I've Verified My Email
              </Button>

              <Button
                onClick={handleResendEmail}
                variant="outline"
                className="w-full"
                disabled={isResending || resendCooldown > 0}
              >
                {isResending ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : resendCooldown > 0 ? (
                  `Resend in ${resendCooldown}s`
                ) : (
                  <>
                    <Mail className="w-4 h-4 mr-2" />
                    Resend Verification Email
                  </>
                )}
              </Button>

              <Button
                onClick={handleSignOut}
                variant="ghost"
                className="w-full"
              >
                Sign Out
              </Button>
            </div>

            <div className="text-sm text-muted-foreground space-y-2">
              <p className="font-semibold">Haven't received the email?</p>
              <ul className="list-disc list-inside space-y-1 text-xs">
                <li>Check your spam or junk folder</li>
                <li>Make sure {user.email} is correct</li>
                <li>Wait a few minutes for the email to arrive</li>
                <li>Try resending the verification email</li>
              </ul>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Email is verified - render children
  return <>{children}</>;
}
